import React, { useContext, useEffect, useState, useCallback } from "react";
import { UserContext } from "../../../contexts/CurrentUserContext";
import {
  getPaymentsProgressApproval,
  getPaymentsProgressApprovalsByApprover,
  getPaymentsProgressGet,
  getPaymentsProgressUserProjects,
} from "../../../api/paymentsProgressApi";
import { updateSketchDesignApproverStatus } from "../../../api/projectEstimatesApi";
import Preloader from "../../Preloader/Preloader";
import "./PaymentsProgressApproval.css";

const MONTH_NAMES = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

function formatUserName(user, userIdFallback) {
  const name = [user?.surname, user?.name].filter(Boolean).join(" ").trim();
  if (name) return name;
  if (userIdFallback != null) return `ID ${userIdFallback}`;
  return "—";
}

function formatMoney(value) {
  if (value == null || value === "" || Number.isNaN(Number(value))) return "–";
  return Number(value).toLocaleString("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function PaymentsProgressApproval() {
  const { userData } = useContext(UserContext);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [detailByApprovalId, setDetailByApprovalId] = useState({});

  useEffect(() => {
    let cancelled = false;
    const fetchApprovals = async () => {
      if (!userData?.id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const approvals = await getPaymentsProgressApprovalsByApprover();

        if (cancelled) return;

        const uniq = new Map();
        // API возвращает либо массив согласований, либо обёртки { approval }; бейдж считает по «плоским» объектам.
        const list = (Array.isArray(approvals) ? approvals : [])
          .map((entry) => (entry?.approval != null ? entry.approval : entry))
          .filter((approval) => approval?.approvers?.length)
          .map((approval) => {
            const myApprover = approval.approvers.find(
              (a) =>
                a.user?.id === userData.id ||
                String(a.user_id) === String(userData.id),
            );
            if (!myApprover) return null;
            return {
              id: approval.id,
              approval,
              myApprover,
              targetUser: approval.user,
              creator: approval.creator,
              month: approval.month,
              year: approval.year,
            };
          })
          .filter(Boolean)
          .filter((item) => {
            if (uniq.has(item.id)) return false;
            uniq.set(item.id, true);
            return true;
          })
          .sort((a, b) => {
            const aKey = `${a.year}-${String(a.month).padStart(2, "0")}`;
            const bKey = `${b.year}-${String(b.month).padStart(2, "0")}`;
            return bKey.localeCompare(aKey);
          });

        setItems(list);
        setExpandedIds(new Set());
        setDetailByApprovalId({});
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || "Ошибка при загрузке согласований выплат");
          setItems([]);
          setExpandedIds(new Set());
          setDetailByApprovalId({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchApprovals();
    return () => {
      cancelled = true;
    };
  }, [userData?.id]);

  const ensurePaymentBreakdown = useCallback(async (item) => {
    const key = item.id;
    let skip = false;
    setDetailByApprovalId((prev) => {
      if (prev[key]?.loaded || prev[key]?.loading) {
        skip = true;
        return prev;
      }
      return {
        ...prev,
        [key]: { loading: true, loaded: false, rows: [], error: null },
      };
    });
    if (skip) return;

    try {
      const userId = Number(item.targetUser?.id ?? item.approval?.user_id);
      if (!userId || Number.isNaN(userId)) {
        throw new Error("Не указан сотрудник для выплаты");
      }

      const projects = await getPaymentsProgressUserProjects(
        item.month,
        item.year,
        userId,
      );
      const projectList = Array.isArray(projects) ? projects : [];

      const rows = await Promise.all(
        projectList.map(async (project) => {
          const statsRaw = await getPaymentsProgressGet(
            item.month,
            item.year,
            userId,
            project.id,
          );
          const stats = Array.isArray(statsRaw) ? statsRaw : [];
          let total = 0;
          for (const stat of stats) {
            const corr = stat.correction ?? {};
            const sum =
              corr.completion_sum_after_correction ??
              stat.to_be_payed_by_employee ??
              0;
            total += Number(sum) || 0;
          }
          return {
            projectId: project.id,
            projectName: project.name || `Проект ${project.id}`,
            total,
          };
        }),
      );

      const sorted = [...rows].sort((a, b) => b.total - a.total);

      setDetailByApprovalId((prev) => ({
        ...prev,
        [key]: { loading: false, loaded: true, rows: sorted, error: null },
      }));
    } catch (err) {
      setDetailByApprovalId((prev) => ({
        ...prev,
        [key]: {
          loading: false,
          loaded: false,
          rows: [],
          error: err?.message || "Не удалось загрузить суммы по проектам",
        },
      }));
    }
  }, []);

  const toggleRowExpanded = (item) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      const closing = next.has(item.id);
      if (closing) {
        next.delete(item.id);
      } else {
        next.add(item.id);
        Promise.resolve().then(() => ensurePaymentBreakdown(item));
      }
      return next;
    });
  };

  const getStatusText = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "approved" || s === "согласован") return "Согласован";
    if (s === "rejected" || s === "отклонен") return "Отклонен";
    return "На рассмотрении";
  };

  const isApproverPending = (status) => {
    const s = String(status || "").toLowerCase();
    return (
      !s || s === "in_processing" || s === "pending" || s === "на рассмотрении"
    );
  };

  const handleSetStatus = async (item, status) => {
    if (!item.myApprover?.id) return;
    setUpdatingId(item.id);
    try {
      await updateSketchDesignApproverStatus(item.myApprover.id, status);
      const refreshed = await getPaymentsProgressApproval(
        item.year,
        item.month,
        item.targetUser?.id || item.approval?.user_id,
      );
      const nextMyApprover =
        refreshed?.approvers?.find(
          (a) =>
            a.user?.id === userData?.id ||
            String(a.user_id) === String(userData?.id),
        ) || item.myApprover;
      setItems((prev) =>
        prev.map((i) =>
          i.id === item.id
            ? {
                ...i,
                approval: refreshed || i.approval,
                myApprover: nextMyApprover,
              }
            : i,
        ),
      );
    } catch (err) {
      alert(err?.message || "Ошибка обновления статуса");
    } finally {
      setUpdatingId(null);
    }
  };

  if (loading) return <Preloader />;

  return (
    <div className="pp-approval">
      <h2 className="pp-approval__title">Согласование выплат ЭП</h2>

      {error && <div className="pp-approval__error">{error}</div>}

      {!error && items.length === 0 && (
        <div className="pp-approval__empty">
          Нет согласований выплат на ваше утверждение
        </div>
      )}

      {!error && items.length > 0 && (
        <div className="pp-approval__table-wrap">
          <p className="pp-approval__hint">
            Нажмите на строку, чтобы посмотреть суммы к выплате по проектам (как
            на прогрессе выплат: с учётом корректировки руководителя).
          </p>
          <table className="pp-approval__table">
            <thead>
              <tr>
                <th>Сотрудник</th>
                <th>Инициатор</th>
                <th>Период</th>
                <th>Согласующие</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const status = item.myApprover?.status;
                const isPending = isApproverPending(status);
                const userName =
                  [item.targetUser?.surname, item.targetUser?.name]
                    .filter(Boolean)
                    .join(" ")
                    .trim() ||
                  `ID ${item.targetUser?.id || item.approval?.user_id}`;
                const creatorName =
                  [item.creator?.surname, item.creator?.name]
                    .filter(Boolean)
                    .join(" ")
                    .trim() || "—";
                const period = `${MONTH_NAMES[(item.month || 1) - 1]} ${item.year || ""}`;
                const isUpdating = updatingId === item.id;
                const approversList = item.approval?.approvers || [];
                const expanded = expandedIds.has(item.id);
                const breakdown = detailByApprovalId[item.id];
                const breakdownRows = breakdown?.rows ?? [];
                const grandTotal = breakdownRows.reduce(
                  (acc, r) => acc + (Number(r.total) || 0),
                  0,
                );

                return (
                  <React.Fragment key={item.id}>
                    <tr
                      className="pp-approval__row-expandable"
                      onClick={() => toggleRowExpanded(item)}
                      title="Показать или скрыть суммы по проектам"
                    >
                      <td>
                        <span
                          className={`pp-approval__chevron ${expanded ? "pp-approval__chevron--open" : ""}`}
                          aria-hidden
                        >
                          ›
                        </span>
                        {userName}
                      </td>
                      <td>{creatorName}</td>
                      <td>{period}</td>
                      <td className="pp-approval__cell--approvers">
                        <ul className="pp-approval__approvers-list">
                          {approversList.map((approver) => (
                            <li
                              key={
                                approver.id ??
                                `${approver.user_id}-${approver.approval_id}`
                              }
                            >
                              <span className="pp-approval__approver-name">
                                {formatUserName(
                                  approver.user,
                                  approver.user_id,
                                )}
                              </span>
                              <span className="pp-approval__approver-status">
                                {getStatusText(approver.status)}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="pp-approval__actions">
                          {isPending && (
                            <>
                              <button
                                type="button"
                                className="pp-approval__btn-approve"
                                onClick={() =>
                                  handleSetStatus(item, "approved")
                                }
                                disabled={isUpdating}
                              >
                                {isUpdating ? "..." : "Согласовать"}
                              </button>
                              <button
                                type="button"
                                className="pp-approval__btn-reject"
                                onClick={() =>
                                  handleSetStatus(item, "rejected")
                                }
                                disabled={isUpdating}
                              >
                                Отклонить
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="pp-approval__breakdown-row">
                        <td colSpan={5}>
                          <div className="pp-approval__breakdown">
                            {breakdown?.loading && (
                              <div className="pp-approval__breakdown-loading">
                                <Preloader />
                              </div>
                            )}
                            {!breakdown?.loading && breakdown?.error && (
                              <div className="pp-approval__breakdown-error">
                                {breakdown.error}
                              </div>
                            )}
                            {!breakdown?.loading &&
                              !breakdown?.error &&
                              breakdownRows.length === 0 && (
                                <div className="pp-approval__breakdown-empty">
                                  Нет проектов с начислениями за этот период.
                                </div>
                              )}
                            {!breakdown?.loading &&
                              !breakdown?.error &&
                              breakdownRows.length > 0 && (
                                <table className="pp-approval__breakdown-table">
                                  <thead>
                                    <tr>
                                      <th>Проект</th>
                                      <th className="pp-approval__money">
                                        К выплате по проекту (итого)
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {breakdownRows.map((row) => (
                                      <tr key={row.projectId}>
                                        <td>{row.projectName}</td>
                                        <td className="pp-approval__money">
                                          ₸ {formatMoney(row.total)}
                                        </td>
                                      </tr>
                                    ))}
                                    <tr className="pp-approval__breakdown-total">
                                      <td>Всего по сотруднику за период</td>
                                      <td className="pp-approval__money">
                                        ₸ {formatMoney(grandTotal)}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                              )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default PaymentsProgressApproval;
